import React from 'react';

const ScoreProgressBar = ({ score, label, weight, color }) => {
  const getScoreLevel = (score) => {
    if (score >= 90) return { level: 'Excellent', color: 'text-green-600', bgColor: 'bg-green-500' };
    if (score >= 80) return { level: 'Very Good', color: 'text-green-600', bgColor: 'bg-green-500' };
    if (score >= 70) return { level: 'Good', color: 'text-yellow-600', bgColor: 'bg-yellow-500' };
    if (score >= 60) return { level: 'Fair', color: 'text-yellow-600', bgColor: 'bg-yellow-500' };
    return { level: 'Needs Work', color: 'text-red-600', bgColor: 'bg-red-500' };
  };

  const scoreLevel = getScoreLevel(score);

  return (
    <div className="mb-6 last:mb-0">
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center">
          <span className="font-semibold text-gray-900">{label}</span>
          {weight && (
            <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
              {weight}% weight
            </span>
          )}
        </div>
        <div className="flex items-center space-x-3">
          <span className={`text-sm font-medium ${scoreLevel.color}`}>
            {scoreLevel.level}
          </span>
          <span className="text-2xl font-bold text-gray-900">
            {score}<span className="text-base text-gray-500">/100</span>
          </span>
        </div>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
        <div
          className={`${scoreLevel.bgColor} h-3 rounded-full transition-all duration-1000 ease-out`}
          style={{ width: `${score}%` }}
        />
      </div>
    </div>
  );
};

const ResumeScoreCard = ({ scores, status }) => {
  const getOverallScoreDetails = (score) => {
    if (score >= 90) {
      return {
        level: 'Excellent',
        color: 'from-green-500 to-emerald-500',
        textColor: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        message: 'Your resume is highly competitive and well-optimized.',
        icon: (
          <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        )
      };
    }
    if (score >= 80) {
      return {
        level: 'Very Good',
        color: 'from-green-500 to-teal-500',
        textColor: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        message: 'Your resume is strong with minor improvements needed.',
        icon: (
          <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        )
      };
    }
    if (score >= 70) {
      return {
        level: 'Good',
        color: 'from-yellow-500 to-orange-500',
        textColor: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        message: 'Your resume is solid but could benefit from optimization.',
        icon: (
          <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        )
      };
    }
    if (score >= 60) {
      return {
        level: 'Fair',
        color: 'from-orange-500 to-red-500',
        textColor: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        message: 'Your resume needs improvement in several areas.',
        icon: (
          <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        )
      };
    }
    return {
      level: 'Needs Work',
      color: 'from-red-500 to-pink-500',
      textColor: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      message: 'Your resume requires significant improvements to be competitive.',
      icon: (
        <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      )
    };
  };

  const overallDetails = getOverallScoreDetails(scores.overall_score);

  return (
    <div className="space-y-6">
      {/* Overall Score - Large Hero Card */}
      <div className={`${overallDetails.bgColor} border-2 ${overallDetails.borderColor} rounded-xl p-8`}>
        <div className="flex flex-col md:flex-row items-center justify-between">
          <div className="flex items-center space-x-6 mb-6 md:mb-0">
            <div className={overallDetails.textColor}>
              {overallDetails.icon}
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Overall Score: {scores.overall_score}/100
              </h2>
              <p className={`text-xl font-semibold ${overallDetails.textColor} mb-2`}>
                {overallDetails.level}
              </p>
              <p className="text-gray-700">
                {overallDetails.message}
              </p>
              {status === 'finalized' && (
                <div className="mt-3 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Finalized
                </div>
              )}
            </div>
          </div>

          {/* Circular Progress */}
          <div className="relative">
            <svg className="w-32 h-32 transform -rotate-90">
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="currentColor"
                strokeWidth="8"
                fill="transparent"
                className="text-gray-300"
              />
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="currentColor"
                strokeWidth="8"
                fill="transparent"
                strokeDasharray={`${2 * Math.PI * 56}`}
                strokeDashoffset={`${2 * Math.PI * 56 * (1 - scores.overall_score / 100)}`}
                className={`${overallDetails.textColor} transition-all duration-1000`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-3xl font-bold text-gray-900">{scores.overall_score}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Score Breakdown */}
      <div className="bg-white rounded-xl shadow-lg p-8">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Score Breakdown</h3>

        <ScoreProgressBar
          score={scores.ats_score}
          label="ATS Optimization"
          weight={30}
          color="blue"
        />

        <ScoreProgressBar
          score={scores.content_quality_score}
          label="Content Quality"
          weight={30}
          color="purple"
        />

        <ScoreProgressBar
          score={scores.completeness_score}
          label="Completeness"
          weight={25}
          color="indigo"
        />

        <ScoreProgressBar
          score={scores.format_score}
          label="Format & Readability"
          weight={15}
          color="teal"
        />

        {/* Score Methodology */}
        <div className="mt-8 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-2">How Scoring Works</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li className="flex items-start">
              <span className="text-blue-500 mr-2">•</span>
              <span><strong>ATS Optimization (30%):</strong> Keywords, measurable achievements, ATS-friendly formatting</span>
            </li>
            <li className="flex items-start">
              <span className="text-purple-500 mr-2">•</span>
              <span><strong>Content Quality (30%):</strong> Action verbs, quantified results, impact statements</span>
            </li>
            <li className="flex items-start">
              <span className="text-indigo-500 mr-2">•</span>
              <span><strong>Completeness (25%):</strong> All sections present with adequate detail</span>
            </li>
            <li className="flex items-start">
              <span className="text-teal-500 mr-2">•</span>
              <span><strong>Format (15%):</strong> Consistency, readability, appropriate length</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ResumeScoreCard;
